import { Component, computed, signal, effect } from '@angular/core';
import { catchError, of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../../core/auth.service';
import { GeoService } from '../../modules/api';
import { RealtimeService } from '../../core/realtime.service';
import { AuthActionService } from '../../core/services/auth-action.service';
import { NotificationUiService } from '../notifications/notification-ui.service';
import { AvatarComponent } from '../avatar/avatar.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule, 
    RouterLink, 
    MatToolbarModule, 
    MatButtonModule, 
    MatIconModule, 
    MatMenuModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    AvatarComponent
  ],
  template: `
    <mat-toolbar color="primary" class="header-toolbar">
      <div class="toolbar-content">
        <a routerLink="/" class="logo">
          <img src="/assets/logo.svg" alt="МКВМ" class="logo-image" />
          <span class="logo-text-wrap">
            <span class="logo-text">Магазин квадратных метров</span>
            <span class="logo-subtext">Недвижимость точно под вашу потребность — умный подбор с ИИ</span>
          </span>
        </a>
    
        <nav class="nav-links">
        </nav>
    
        <div class="header-actions">
          @if (isLoggedIn()) {
            <a mat-icon-button class="hide-on-compact" [routerLink]="['/catalog']" [queryParams]="{ favorites: 1 }" aria-label="Избранное">
              <mat-icon>favorite_border</mat-icon>
            </a>
            <a mat-icon-button class="hide-on-narrow" routerLink="/chat" aria-label="Сообщения">
              <mat-icon>chat_bubble_outline</mat-icon>
            </a>
          } @else {
            <button mat-icon-button class="hide-on-compact" type="button" (click)="openFavoritesAction()" aria-label="Избранное">
              <mat-icon>favorite_border</mat-icon>
            </button>
            <button mat-icon-button class="hide-on-narrow" type="button" (click)="openChatAction()" aria-label="Сообщения">
              <mat-icon>chat_bubble_outline</mat-icon>
            </button>
          }
    
          @if (isLoggedIn()) {
            <!-- Notifications Bell -->
            <button mat-icon-button [matMenuTriggerFor]="notificationsMenu" (click)="notificationUi.markAllRead()">
              <mat-icon>notifications_none</mat-icon>
              @if (notificationUi.unreadCount() > 0) {
                <div class="notification-badge">{{notificationUi.unreadCount()}}</div>
              }
            </button>
    
            <mat-menu #notificationsMenu="matMenu">
              <div class="notifications-dropdown">
                <h3>Уведомления</h3>
                @if (notificationUi.notifications().length > 0) {
                  <div class="notifications-list">
                    @for (notification of notificationUi.notifications(); track notification) {
                      <div class="notification-item" (click)="notificationUi.open(notification)">
                        <div class="notification-content">
                          <div class="notification-title">{{notificationUi.getTitle(notification)}}</div>
                          @if (notification.payload?.['text_preview']) {
                            <div class="notification-preview">{{ $any(notification.payload)['text_preview'] }}</div>
                          }
                          <div class="notification-time">{{ notification.created_at | date:'dd.MM.yyyy, HH:mm' }}</div>
                        </div>
                      </div>
                    }
                  </div>
                }
                @if (notificationUi.notifications().length === 0) {
                  <div class="empty-notifications">
                    Нет уведомлений
                  </div>
                }
                @if (notificationUi.notifications().length > 0) {
                  <button mat-raised-button color="primary" class="mark-all-read" (click)="notificationUi.markAllRead()">
                    Прочитать все
                  </button>
                }
              </div>
            </mat-menu>
          } @else {
            <button mat-icon-button type="button" (click)="openNotificationsAction()" aria-label="Уведомления">
              <mat-icon>notifications_none</mat-icon>
            </button>
          }
     
          <button mat-raised-button class="hide-on-compact" color="accent" type="button" (click)="openCreatePropertyAction()">+Объявление</button>

    
          @if (isLoggedIn()) {
            <button mat-icon-button [matMenuTriggerFor]="userMenu">
              <app-avatar
                [name]="getUserName()"
                [email]="user()?.email || ''"
                [image]="user()?.avatar_url || null"
                />
            </button>
            <mat-menu #userMenu="matMenu">
              <a mat-menu-item routerLink="/profile">Профиль</a>
              <a mat-menu-item routerLink="/chat">Сообщения</a>
              <a mat-menu-item routerLink="/notifications">Уведомления</a>
              <a mat-menu-item routerLink="/my-properties">Мои объявления</a>
              <a mat-menu-item routerLink="/create-property">Добавить объявление</a>
              @if (isAdmin()) {
                <a mat-menu-item routerLink="/admin/portal">Portal Admin</a>
              }
              @if (isAdmin() || isModerator()) {
                <a mat-menu-item routerLink="/moderation">Модерация</a>
              }
              <button mat-menu-item (click)="logout()">Выход</button>
            </mat-menu>
          } @else {
            <button mat-button type="button" (click)="openLoginAction()">Вход</button>
          }
        </div>
      </div>
    </mat-toolbar>
    `,
  styles: [
    `
    .header-toolbar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1000;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .toolbar-content {
      max-width: 1400px;
      margin: 0 auto;
      padding: 0 1rem;
      display: flex;
      align-items: center;
      width: 100%;
      gap: 2rem;
    }
    .logo {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      text-decoration: none;
      color: #fff;
      font-weight: 700;
      font-size: 1.1rem;
    }
    .logo-image {
      height: 36px;
      width: 36px;
      filter: brightness(0) invert(1);
    }
    .logo-text-wrap {
      display: none;
      flex-direction: column;
      line-height: 1.05;
    }
    .logo-text {
      font-size: 1.2rem;
      font-weight: 700;
      color: #fff;
    }
    .logo-subtext {
      font-size: 0.68rem;
      font-weight: 400;
      color: rgba(255,255,255,0.72);
      margin-top: 0.1rem;
    }
    @media (min-width: 480px) {
      .logo-text-wrap {
        display: flex;
      }
    }
    .region-selector {
      display: none;
    }
    .region-field {
      width: 100%;
    }
    .nav-links {
      display: flex;
      gap: 1.5rem;
    }
    .nav-link {
      text-decoration: none;
      color: rgba(255,255,255,0.85);
      font-weight: 500;
      transition: color 0.2s;
    }
    .nav-link:hover {
      color: #fff;
    }
    .header-actions {
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .header-actions button[mat-icon-button] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }

     .header-actions button[mat-icon-button] app-avatar {
       display: inline-flex;
       align-items: center;
       justify-content: center;
       margin: 0;
     }
     /*
      * Header actions should shrink progressively on compact widths instead of wrapping
      * into a second row. Lower-priority actions are hidden first and remain available
      * through the user menu, so the toolbar keeps a single-line layout.
      */
     .hide-on-compact,
     .hide-on-narrow {
       display: inline-flex;
     }

     @media (max-width: 960px) {
       .toolbar-content {
         gap: 1rem;
       }

       .logo-subtext {
         display: none;
       }

       .header-actions {
         gap: 0.25rem;
       }

       .hide-on-compact {
         display: none !important;
       }
     }

     @media (max-width: 640px) {
       .toolbar-content {
         padding: 0 0.75rem;
       }

       .logo-text {
         font-size: 1rem;
       }

       .hide-on-narrow {
         display: none !important;
       }
     }

     @media (max-width: 480px) {
       .toolbar-content {
         gap: 0.75rem;
       }

       .logo-text-wrap {
         display: none;
       }
     }
     /* User menu panel shadow */

    mat-menu {
      --mat-menu-container-color: #fff;
    }
    ::ng-deep .mat-mdc-menu-panel {
      box-shadow: 0 4px 6px rgba(0,0,0,0.16), 0 2px 4px rgba(0,0,0,0.23);
      border-radius: 8px;
      padding: 8px 0;
      margin-top: 4px;
      max-width: 280px !important;
      width: 280px !important;
      overflow-x: hidden;
    }
    .notification-badge {
      position: absolute;
      top: -8px;
      right: -8px;
      background: #f44336;
      color: white;
      border-radius: 50%;
      padding: 2px 6px;
      font-size: 12px;
      min-width: 18px;
      text-align: center;
    }
    
    /* Notifications dropdown styles */
    .notifications-dropdown {
      width: 100%;
      max-width: 100%;
      padding: 12px;
      overflow: hidden;
      box-sizing: border-box;
    }
    
    .notifications-dropdown h3 {
      margin-top: 0;
      margin-bottom: 8px;
      color: #333;
      font-size: 14px;
    }
    
    .notifications-list {
      max-height: 260px;
      overflow-y: auto;
      overflow-x: hidden;
      scrollbar-width: none;
      -ms-overflow-style: none;
      padding-right: 0;
      width: 100%;
      box-sizing: border-box;
    }

    .notifications-list::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none;
    }
    
    .notification-item {
      padding: 8px 10px;
      border-bottom: 1px solid #eee;
      cursor: pointer;
      overflow: hidden;
    }
    
    .notification-item:hover {
      background-color: #f5f5f5;
    }
    
    .notification-content {
      display: flex;
      flex-direction: column;
      min-width: 0;
      width: 100%;
    }
    
    .notification-title {
      font-weight: 500;
      margin-bottom: 2px;
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .notification-time {
      font-size: 11px;
      color: #666;
      line-height: 1.1;
    }

    .notification-preview {
      font-size: 11px;
      color: #555;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 2px;
      max-width: 100%;
      line-height: 1.2;
    }
    
    .empty-notifications {
      padding: 10px;
      text-align: center;
      color: #666;
      font-size: 12px;
    }
    
    .mark-all-read {
      width: auto;
      align-self: flex-start;
      margin-top: 8px;
      font-size: 12px;
      padding: 0 12px;
      min-width: 0;
    }
    `
  ]
})
export class HeaderComponent {
  readonly isLoggedIn = this.authService.isLoggedIn;
  readonly userRole = this.authService.userRole;
  readonly user = this.authService.user;
    
  readonly isAdmin = computed(() => this.userRole() === 'admin');
  readonly isModerator = computed(() => this.userRole() === 'moderator');
  
  regions = signal<string[]>([
    'Санкт-Петербург',
    'Ленинградская область'
  ]);
  selectedRegion = signal('Санкт-Петербург');

  private readonly isBrowser = typeof window !== 'undefined';

  constructor(
    private authService: AuthService, 
    private authAction: AuthActionService,
    public notificationUi: NotificationUiService,
    private realtime: RealtimeService,
    private geoService: GeoService,
    private router: Router
  ) {
    this.loadStoredRegion();

    effect(() => {
      const loggedIn = this.isLoggedIn();
      if (!this.isBrowser) return;

      if (loggedIn) {
        this.realtime.connect();
        this.realtime.subscribeTopics([
          { topic: 'notifications', user_id: 'self' },
          { topic: 'chat.thread_list', user_id: 'self' },
        ]);
        this.notificationUi.load();
      } else {
        this.realtime.disconnect();
      }
    });

    if (this.isBrowser) {
      this.detectRegionByIP();
      this.realtime.notifications$.subscribe((notification) => {
        console.log('[header] notification_created', notification);
      });
    }
  }

  loadStoredRegion() {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('selectedRegion');
      if (saved) {
        this.selectedRegion.set(saved);
      }
    }
  }

  detectRegionByIP() {
    this.geoService.getCitiesApiGeoCitiesGet().pipe(catchError(() => of([]))).subscribe({
      next: (cities) => {
        if (cities.length > 0) {
          // Already have cities loaded
        }
      },
      error: () => {}
    });

    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('selectedRegion');
      if (!saved) {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        const region = timezone.includes('Europe/Moscow') ? 'Санкт-Петербург' : 'Ленинградская область';
        this.selectedRegion.set(region);
        localStorage.setItem('selectedRegion', region);
      }
    }
  }

  onRegionChange(region: string) {
    this.selectedRegion.set(region);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('selectedRegion', region);
    }
    this.router.navigate(['/catalog'], {
      queryParams: { region }
    });
  }

  logout() {
    this.authService.logout();
  }

  async openLoginAction() {
    await this.authAction.ensureLoggedIn();
  }

  async openFavoritesAction() {
    const ok = await this.authAction.ensureLoggedIn();
    if (ok) {
      await this.router.navigate(['/catalog'], { queryParams: { favorites: 1 } });
    }
  }

  async openChatAction() {
    const ok = await this.authAction.ensureLoggedIn();
    if (ok) {
      await this.router.navigate(['/chat']);
    }
  }

  async openNotificationsAction() {
    const ok = await this.authAction.ensureLoggedIn();
    if (ok) {
      await this.router.navigate(['/notifications']);
    }
  }

  async openCreatePropertyAction() {
    const ok = await this.authAction.ensureLoggedIn();
    if (ok) {
      await this.router.navigate(['/create-property']);
    }
  }

  getUserName(): string {
    const user = this.user();
    if (!user) return 'Пользователь';
    
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    
    if (user.email) {
      return user.email.split('@')[0] || 'Пользователь';
    }
    
    return 'Пользователь';
  }
}
